import {
  ActionIcon,
  Alert,
  AppShell,
  Button,
  Checkbox,
  Flex,
  Group,
  Indicator,
  Loader,
  Menu,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { INotification } from "@mctypes/global.types";
import { IMaconomyProject, IProject } from "@schemas/projectSchema";
import {
  IconArchive,
  IconArchiveFilled,
  IconAlertTriangle,
  IconBrandTeams,
  IconBriefcaseFilled,
  IconFileCheck,
  IconFilter2Search,
  IconHelpCircle,
  IconLogout,
  IconMail,
  IconMailOpened,
  IconSettings,
  IconSocial,
  IconTestPipe,
  IconX,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useGetProjectsMaconomyQuery, useGetProjectsMcappsQuery } from "../../openapi";
import { Avatar } from "../components";
import ContactForm from "../components/ContactForm/ContactForm";
import { McSelect } from "../components/McSelect/McSelect";
import NotificationCard from "../components/NotificationCard/NotificationCard";
import SidePanel from "../components/SidePanel/SidePanel";
import TaskBanner from "../components/TaskBanner/TaskBanner";
import {
  addAppModalThunk,
  logoutThunk,
  selectIsUserMutilconsult,
  setShowNotificationsInRedux,
  setShowSuccessMessageThunk,
  thunkReloadAppThunk,
  toggleAllNotificationsAsReadThunk,
  toggleNotificationAndReplaceNotificationThunk,
} from "../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../hooks";
import { languageOptions, useLanguage, useTranslation } from "../i18n";
import { AppModalEnums } from "@mcapps/shared/sharedConstants";
import appShellCss from "./McAppShell.module.css";
import { IExternalIssueBanner, useGetActiveExternalIssueBannersQuery } from "./externalIssueBannerRTK";
import menuCss from "./McAppshellMenu.module.css";
import { useGetNotificationIssueStatusesQuery } from "./notificationRTK";
import { NotificationButton } from "./NotificationsButton";
import { selectMergedProjectList } from "./projectSelectors";
import { doCreateOrJoinProject, doSendUserMessage } from "./routes/admin/restQueries";
import { isLoggedinUserMulticonsultUser } from "./routes/admin/selectors";
import { ISSUE_STATES_OPTIONS } from "./routes/issuesApp/issueConstants";
import UserSettingsModalContent from "./user/UserSettingsModalContent";

type TMcAppShell = (props: { useShell?: boolean; children: React.ReactNode }) => JSX.Element;

export const McAppShell: TMcAppShell = ({ children }) => {
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const showNotification = useAppSelector((state) => state.globalReducer.showNotifications);
  const [showMarkedAsRead, setShowMarkedAsRead] = useLocalStorage("showMarkedAsRead", true);
  const [notificationStatusFilters, setNotificationStatusFilters] = useLocalStorage<string[]>(
    "notificationStatusFilters",
    [],
  );
  const user = useAppSelector((state) => state.globalReducer.user);
  let history = useHistory();

  const notificationIssueIds = useMemo(() => {
    const issueIds =
      user?.notifications
        ?.map((notification) => notification.itemDBid)
        .filter((itemDBid) => /^[0-9a-fA-F]{24}$/.test(itemDBid)) ?? [];

    return [...new Set(issueIds)];
  }, [user?.notifications]);

  const { data: notificationIssueStatuses = [] } = useGetNotificationIssueStatusesQuery(
    { issueIds: notificationIssueIds },
    { skip: notificationIssueIds.length === 0 },
  );

  const notificationStatusByIssueId = useMemo(
    () => new Map(notificationIssueStatuses.map((item) => [item.issueId, item.status])),
    [notificationIssueStatuses],
  );

  const getNotificationIssueStatus = (notification: INotification) =>
    notification.issueStatus ?? notificationStatusByIssueId.get(notification.itemDBid);

  const toggleNotificationStatusFilter = (status: string) => {
    setNotificationStatusFilters((currentFilters) =>
      currentFilters.includes(status)
        ? currentFilters.filter((currentStatus) => currentStatus !== status)
        : [...currentFilters, status],
    );
  };

  const toggleMarkedAsRead = (notification: INotification) => {
    dispatch(toggleNotificationAndReplaceNotificationThunk(notification._id));
  };

  const checkCondition = (notification: INotification) => {
    const readFilterMatches = showMarkedAsRead === true ? true : !notification.markedAsRead.value;
    const issueStatus = getNotificationIssueStatus(notification);
    const statusFilterMatches =
      notificationStatusFilters.length === 0 ||
      (issueStatus != null && notificationStatusFilters.includes(issueStatus));

    return readFilterMatches && statusFilterMatches;
  };

  const markAllNotificationsAsRead = () => {
    dispatch(toggleAllNotificationsAsReadThunk());
  };

  const openModalAndMarkAsRead = (notification: INotification) => {
    const _project = notification.project as IProject;
    if (notification.markedAsRead?.value === false) {
      dispatch(toggleNotificationAndReplaceNotificationThunk(notification._id));
    }
    if (notification.app === "DASHBOARD") {
      history.push(`/project/${_project._id}/dashboard/?layout=${notification.itemDBid}`);
      return;
    }
    if (notification.app === "MEETING") {
      history.push(`/project/${_project._id}/meetings/${notification.boardId}`);
      return;
    }

    if (notification.app === "CHECKLIST") {
      history.push(`/project/${_project._id}/checklist/instance/${notification.boardId}?id=${notification.itemDBid}`);
      return;
    }
    dispatch(
      addAppModalThunk({
        itemId: notification.itemDBid,
        boardId: notification.boardId,
        projectId: _project._id,
        app: AppModalEnums.ISSUE,
      }),
    );
  };

  const hideAppShell = history.location.search.includes("doksam=true");

  return hideAppShell === true ? (
    <>{children}</>
  ) : (
    <>
      <AppShell h="100dvh">
        <AppShell.Header display="flex" bg={"dark.7"}>
          <AppHeader />
        </AppShell.Header>
        <AppShell.Main h="calc(100dvh - var(--app-shell-header-height, 60px))" className="overflow-hidden">
          <Stack h="100%" gap={0} bg="white">
            <ExternalIssueBanners />
            {!window.location.href.replace(/\/+$/, "").endsWith("tasks") &&
              user?.activeAssignedItemsCount?.overdue > 0 && (
                <TaskBanner
                  userName={user.surname ? user.surname : user.name}
                  overdue={user.activeAssignedItemsCount?.overdue}
                />
              )}
            <Flex gap={0} h="100%" className="overflow-auto">
              {children}
            </Flex>
            <DevBanner />
          </Stack>
        </AppShell.Main>
      </AppShell>
      <SidePanel collapsed={!showNotification}>
        <Flex justify="space-between">
          <Tooltip openDelay={500} label={t("notifications.hide")} withArrow>
            <Button
              variant="outline"
              onClick={() => {
                dispatch(setShowNotificationsInRedux(false));
              }}
            >
              <IconX size={20} />
            </Button>
          </Tooltip>
          <Flex gap="xs">
            <Menu position="bottom-end" withArrow closeOnItemClick={false}>
              <Menu.Target>
                <Button variant="outline" w={52} px={0}>
                  <IconFilter2Search size={20} />
                  <Text component="span" size="xs" ml={4} fw={700} w={8} ta="center">
                    {notificationStatusFilters.length > 0 ? notificationStatusFilters.length : ""}
                  </Text>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => setNotificationStatusFilters([])}>
                  <Checkbox
                    checked={notificationStatusFilters.length === 0}
                    readOnly
                    label={t("notifications.allStatuses")}
                  />
                </Menu.Item>
                <Menu.Divider mb={"xs"} mt={0} />
                {ISSUE_STATES_OPTIONS.map((status) => (
                  <Menu.Item key={status.value} onClick={() => toggleNotificationStatusFilter(status.value)}>
                    <Checkbox
                      checked={notificationStatusFilters.includes(status.value)}
                      readOnly
                      label={
                        <Group gap="xs" wrap="nowrap">
                          <span className="statusdot" style={{ backgroundColor: status.color }} />
                          <span>{status.value}</span>
                        </Group>
                      }
                    />
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            <Tooltip openDelay={500} label={t("notifications.markAllRead")} withArrow>
              <Button variant="outline" onClick={() => markAllNotificationsAsRead()}>
                <IconMailOpened size={20} />
              </Button>
            </Tooltip>
            <Tooltip openDelay={500} label={t("notifications.toggleRead")} withArrow>
              <Button variant="outline" onClick={() => setShowMarkedAsRead(!showMarkedAsRead)}>
                {showMarkedAsRead ? <IconArchiveFilled size={20} /> : <IconArchive size={20} />}
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
        <Stack flex={1} gap="xs" mt="xs" className="overflow-auto">
          {user?.notifications?.map((notification, index) => {
            const notificationWithIssueStatus = {
              ...notification,
              issueStatus: getNotificationIssueStatus(notification),
            };

            return (
              checkCondition(notification) && (
                <NotificationCard
                  key={index}
                  notification={notificationWithIssueStatus}
                  onCardClick={(notification) => openModalAndMarkAsRead(notification)}
                  toggleMarkedAsRead={toggleMarkedAsRead}
                />
              )
            );
          })}
        </Stack>
      </SidePanel>
    </>
  );
};

const externalIssueBannerColor = (severity: IExternalIssueBanner["severity"]) => {
  if (severity === "warning") return "yellow";
  if (severity === "info") return "blue";
  return "red";
};

const ExternalIssueBanners = () => {
  const user = useAppSelector((state) => state.globalReducer.user);
  const { data: banners = [] } = useGetActiveExternalIssueBannersQuery(undefined, {
    skip: user == null,
    pollingInterval: 5 * 60_000,
  });

  if (banners.length === 0) {
    return null;
  }

  return (
    <Stack gap={0}>
      {banners.map((banner) => (
        <Alert
          key={banner._id}
          color={externalIssueBannerColor(banner.severity)}
          variant="filled"
          radius={0}
          py={6}
          px="md"
          styles={{ body: { width: "100%" }, message: { width: "100%" } }}
        >
          <Group justify="center" gap="xs" wrap="nowrap">
            <IconAlertTriangle size={18} />
            <Text c="white" fw={700} fz="sm">
              {banner.message}
            </Text>
          </Group>
        </Alert>
      ))}
    </Stack>
  );
};

export const DevBanner = () => {
  const t = useTranslation();
  const history = useHistory();
  const isProduction = useAppSelector((state) => state.globalReducer.isProduction);

  if (isProduction !== false) {
    return null;
  }

  const dispatch = useAppDispatch();
  const [showDevBanner, setShowDevBanner] = useState(true);

  if (showDevBanner) {
    return (
      <Alert
        color="red"
        variant="filled"
        radius={0}
        py={4}
        px="xs"
        icon={<IconTestPipe size={16} />}
        styles={{ body: { width: "100%" } }}
      >
        <Group justify="space-between" align="center" gap="sm">
          <Group gap="xs">
            <Button color="red" variant="white" size="xs" onClick={() => dispatch(thunkReloadAppThunk(history))}>
              {t("dev.resetAppState")}
            </Button>
          </Group>

          <Text c="white" fw={700} fz="sm">
            {t("dev.testEnvironment")}
          </Text>

          <ActionIcon
            variant="subtle"
            color="white"
            size="sm"
            onClick={() => setShowDevBanner(false)}
            aria-label={t("common.close")}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Alert>
    );
  }

  return null;
};

const AppHeader = () => {
  const t = useTranslation();
  let history = useHistory();
  const { project } = useAppSelector((state) => state.adminReducer);
  const [loading, setLoading] = useState(false);
  const isMulticonsultUser = useSelector(isLoggedinUserMulticonsultUser);

  useGetProjectsMcappsQuery();
  useGetProjectsMaconomyQuery(undefined, { skip: isMulticonsultUser === false });

  const mergedProjects = useAppSelector(selectMergedProjectList);

  const goToProject = async (maconomy: IMaconomyProject) => {
    setLoading(true);
    const project = await doCreateOrJoinProject(maconomy);
    setLoading(false);

    if (project.useDashboardAsDefaultStartPage) {
      history.push(`/project/${project._id}/dashboard`);
    } else {
      history.push(`/project/${project._id}/apps`);
    }
  };

  return (
    <Group justify="center" px="md" w="100%">
      <Group className={appShellCss.mainPadding} align="center" justify="space-between" wrap="nowrap">
        <Group className={appShellCss.group}>
          <Flex align="center" to="/" component={Link}>
            <span className="text-white font-weight-bold" style={{ fontSize: "16px" }}>
              <i>MCApps</i>
            </span>
          </Flex>
          <McSelect
            w="20rem"
            classNames={{
              input: menuCss.dropdown,
              dropdown: menuCss.dropdown,
              option: menuCss.item,
              search: menuCss.search,
              empty: menuCss.empty,
              thumb: menuCss.thumb,
            }}
            searchable={true}
            leftSection={
              loading ? (
                <Loader size={16} />
              ) : (
                project?.maconomy?.projectNo != null && <IconBriefcaseFilled color="white" size={16} />
              )
            }
            data={mergedProjects?.map((project) => ({ value: String(project.projectNo), label: project.name })) || []}
            value={project?.maconomy?.projectNo != null ? String(project.maconomy.projectNo) : null}
            onChange={(value) => {
              const selectedProject = mergedProjects.find((project) => String(project.projectNo) === value);
              if (selectedProject) {
                goToProject(selectedProject);
              }
            }}
            placeholder={t("app.projectPlaceholder")}
          />
        </Group>
        <Flex>
          <Flex wrap="nowrap" gap={0} align="center" style={{ userSelect: "none" }}>
            <LanguageSelector />
            <NotificationButton />
            <MyTasksButton />
            <SupportMenu />
          </Flex>
          <UserMenu />
        </Flex>
      </Group>
    </Group>
  );
};

const LanguageSelector = () => {
  const { language, languageSelectionEnabled, setLanguage, t } = useLanguage();

  if (!languageSelectionEnabled) {
    return null;
  }

  return (
    <Tooltip label={t("common.language")} withArrow>
      <SegmentedControl
        aria-label={t("common.language")}
        size="xs"
        value={language}
        onChange={(value) => setLanguage(value as typeof language)}
        data={languageOptions}
        styles={{
          root: {
            backgroundColor: "var(--mantine-color-dark-6)",
            border: "1px solid var(--mantine-color-dark-3)",
          },
          indicator: {
            backgroundColor: "var(--mantine-color-dark-4)",
          },
          label: {
            color: "var(--mantine-color-lightgray-1)",
            fontWeight: 700,
            minWidth: 28,
          },
        }}
      />
    </Tooltip>
  );
};

export const SupportMenu = () => {
  const t = useTranslation();
  const userIsMulticonsult = useAppSelector(selectIsUserMutilconsult);
  const dispatch = useAppDispatch();

  const sendUserMessage = async (message: string, subject: string) => {
    try {
      await doSendUserMessage(subject, message);
      dispatch(setShowSuccessMessageThunk(t("feedback.success")));
      modals.close("contact-form-modal");
    } catch (error) {
      dispatch(setShowSuccessMessageThunk(t("feedback.error")));
    }
  };

  const openSendMessage = () => {
    modals.open({
      id: "contact-form-modal",
      title: t("common.contact"),
      centered: true,
      size: "lg",
      children: <ContactForm sendMessage={sendUserMessage} />,
    });
  };

  const supportItems = {
    teams: {
      name: "Teams",
      link: "https://teams.microsoft.com/l/team/19%3a160b1c8d39394b65b06f619a179f0599%40thread.skype/conversations?groupId=72448e5d-7b2c-4ed5-933b-2d939c4d87a1&tenantId=81ab7478-8d3c-4736-8b54-7f2bfd9fa20e",
    },
    viva: {
      name: "Viva Engage",
      link: "https://web.yammer.com/main/org/multiconsult.no/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIxMTQ1NDg2NjIyNzIifQ",
    },
    email: {
      name: "E-post",
      link: "mailto:mcapps@multiconsult.no",
    },
  };

  return userIsMulticonsult === true ? (
    <Menu
      classNames={{
        dropdown: menuCss.dropdown,
        item: menuCss.item,
        divider: menuCss.divider,
      }}
      withArrow
    >
      <Menu.Target>
        <Tooltip label={t("common.help")} withArrow>
          <Button c="white" size="compact-xl" variant="subtle">
            <div style={{ transform: "scale(1.2)", position: "relative", top: "-2px" }}>
              <IconHelpCircle size={16} />
            </div>
          </Button>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item leftSection={<IconMail size={16} />} onClick={() => openSendMessage()}>
          {supportItems.email.name}
        </Menu.Item>

        <Menu.Item
          leftSection={<IconBrandTeams size={16} />}
          onClick={() =>
            window.open(supportItems.teams.link, "_blank") || window.location.replace(supportItems.teams.link)
          }
        >
          {supportItems.teams.name}
        </Menu.Item>

        <Menu.Item
          leftSection={<IconSocial size={16} />}
          onClick={() =>
            window.open(supportItems.viva.link, "_blank") || window.location.replace(supportItems.viva.link)
          }
        >
          {supportItems.viva.name}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <Button leftSection={<IconHelpCircle size={18} />} onClick={() => openSendMessage()}>
      {t("support.contactSupport")}
    </Button>
  );
};

export const UserMenu = () => {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.globalReducer.user);

  const logout = () => {
    dispatch(logoutThunk());
  };

  const openUserSettings = () => {
    modals.open({
      title: t("common.userSettings"),
      centered: true,
      size: "40vw",
      radius: "lg",
      children: <UserSettingsModalContent />,
    });
  };

  return (
    <>
      <Menu
        position="bottom-end"
        withArrow
        classNames={{
          dropdown: menuCss.dropdown,
          item: menuCss.item,
          divider: menuCss.divider,
        }}
      >
        <Menu.Target>
          <Button variant="subtle" style={{ padding: 0, background: "none" }}>
            <Avatar id={user._id} name={user.name} size="sm" />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={openUserSettings} leftSection={<IconSettings size={16} />}>
            {t("common.settings")}
          </Menu.Item>
          <Menu.Item onClick={logout} leftSection={<IconLogout size={16} />}>
            {t("user.logout")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

export const MyTasksButton = () => {
  const t = useTranslation();
  const user = useAppSelector((state) => state.globalReducer.user);
  const assignedItems = user?.activeAssignedItemsCount;
  const assignedItemsTotal = assignedItems?.total ?? 0;
  const assignedItemsLabel = assignedItemsTotal > 99 ? "99+" : assignedItemsTotal;
  const navigation = useHistory();

  return (
    <Tooltip label={t("tasks.mine")} withArrow>
      <Indicator
        disabled={assignedItemsTotal === 0}
        inline
        label={assignedItemsLabel}
        size={16}
        offset={10}
        position="top-end"
        color="orange.5"
        styles={{
          indicator: {
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
            minWidth: 16,
            paddingInline: 4,
          },
        }}
      >
        <Button c="white" size="compact-xl" variant="subtle" onClick={() => navigation.push("/tasks")}>
          <IconFileCheck size={20} />
        </Button>
      </Indicator>
    </Tooltip>
  );
};
